import React, { useEffect, useState } from 'react';
import {
  CheckCircle,
  Activity,
  Layers,
  Code2,
  XCircle,
  AlertTriangle,
  TestTube2,
  FileText,
  Play,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { QADashboardStats, EntityCoverageItem } from '../types';
import api from '../services/api';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<QADashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getQADashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load QA stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const trendData = stats?.execution_trend || [
    { week: 'Sem 1', executions: 0, avg_pass_rate: 0 },
    { week: 'Sem 2', executions: 0, avg_pass_rate: 0 },
    { week: 'Sem 3', executions: 0, avg_pass_rate: 0 },
    { week: 'Sem 4', executions: 0, avg_pass_rate: 0 },
  ];

  const passRateColor = (rate: number) =>
    rate >= 80 ? 'text-emerald-400' : rate >= 50 ? 'text-yellow-400' : 'text-red-400';

  const passRateBg = (rate: number) =>
    rate >= 80 ? 'bg-emerald-500' : rate >= 50 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-surface-dark border border-slate-800 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <TestTube2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">QA Dashboard</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Organizaci&oacute;n:</span>
              <span className="text-sm text-primary font-medium">{stats?.org_name}</span>
              {stats?.plan && (
                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium uppercase">
                  {stats.plan}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-6">
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Test Suites</p>
            <p className="text-lg text-white font-bold">{stats?.total_test_suites || 0}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Casos Generados</p>
            <p className="text-lg text-white font-bold">{stats?.total_test_cases_generated || 0}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Entidades"
          value={String(stats?.total_entities || 0)}
          subtitle={`${stats?.entities_with_automation || 0} con código`}
          icon={Layers}
          color="blue"
        />
        <StatCard
          title="Cobertura Automatización"
          value={`${stats?.automation_coverage_pct || 0}%`}
          subtitle={`${stats?.entities_with_automation || 0} / ${stats?.total_entities || 0}`}
          icon={Code2}
          color="purple"
        />
        <StatCard
          title="Pass Rate Global"
          value={`${stats?.overall_pass_rate || 0}%`}
          subtitle={`${stats?.total_tests_run || 0} tests ejecutados`}
          icon={CheckCircle}
          color={
            (stats?.overall_pass_rate || 0) >= 80
              ? 'green'
              : (stats?.overall_pass_rate || 0) >= 50
                ? 'yellow'
                : 'red'
          }
          isHighlight
        />
        <StatCard
          title="Ejecuciones"
          value={String(stats?.total_test_executions || 0)}
          subtitle={`${stats?.entities_with_executions || 0} entidades`}
          icon={Activity}
          color="blue"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Execution Trend Chart */}
        <div className="lg:col-span-2 bg-surface-dark border border-slate-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Tendencia de Ejecuciones</h3>
              <p className="text-sm text-slate-400">Ejecuciones y pass rate de las últimas 4 semanas</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <XAxis
                  dataKey="week"
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  tickFormatter={(v: number) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number, name: string) => {
                    if (name === 'avg_pass_rate') return [`${value}%`, 'Pass Rate'];
                    return [value, 'Ejecuciones'];
                  }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="executions"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#3b82f6' }}
                  name="executions"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avg_pass_rate"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#10b981' }}
                  strokeDasharray="5 5"
                  name="avg_pass_rate"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-6 mt-3 text-xs text-slate-400">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-blue-500 rounded"></span>
              Ejecuciones
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-emerald-500 rounded border-dashed"></span>
              Pass Rate %
            </div>
          </div>
        </div>

        {/* Coverage Breakdown */}
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white">Cobertura</h3>
            <p className="text-sm text-slate-400">Estado de automatización</p>
          </div>
          <div className="space-y-5">
            <CoverageBar
              label="Con Código"
              current={stats?.entities_with_automation || 0}
              total={stats?.total_entities || 0}
              color="purple"
            />
            <CoverageBar
              label="Con Ejecuciones"
              current={stats?.entities_with_executions || 0}
              total={stats?.total_entities || 0}
              color="blue"
            />
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    (stats?.overall_pass_rate || 0) >= 80 ? 'bg-emerald-400' :
                    (stats?.overall_pass_rate || 0) >= 50 ? 'bg-yellow-400' : 'bg-red-400'
                  }`}></span>
                  <span className="text-sm text-slate-300">Pass Rate</span>
                </div>
                <span className="text-sm font-semibold text-white">{stats?.overall_pass_rate || 0}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${passRateBg(stats?.overall_pass_rate || 0)} rounded-full transition-all duration-500`}
                  style={{ width: `${stats?.overall_pass_rate || 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Quick summary numbers */}
          <div className="mt-6 pt-4 border-t border-slate-700 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Tests ejecutados</span>
              <span className="text-white font-medium">{stats?.total_tests_run || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Casos generados</span>
              <span className="text-white font-medium">{stats?.total_test_cases_generated || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Entity Coverage Table */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Cobertura por Entidad</h3>
            <p className="text-sm text-slate-400">Estado de automatización y ejecuciones por historia de usuario</p>
          </div>
        </div>
        {stats?.entities_coverage_list && stats.entities_coverage_list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-3 text-xs text-slate-500 uppercase tracking-wider font-semibold">Entidad</th>
                  <th className="text-center py-2 px-3 text-xs text-slate-500 uppercase tracking-wider font-semibold">Código</th>
                  <th className="text-center py-2 px-3 text-xs text-slate-500 uppercase tracking-wider font-semibold">Ejecuciones</th>
                  <th className="text-center py-2 px-3 text-xs text-slate-500 uppercase tracking-wider font-semibold">Pass Rate</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500 uppercase tracking-wider font-semibold">Runs</th>
                </tr>
              </thead>
              <tbody>
                {stats.entities_coverage_list.map((entity) => (
                  <EntityRow key={entity.entity_id} entity={entity} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No hay entidades registradas</p>
          </div>
        )}
      </div>

      {/* Recent Executions */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Ejecuciones Recientes</h3>
        </div>
        <div className="space-y-3">
          {stats?.recent_executions && stats.recent_executions.length > 0 ? (
            stats.recent_executions.map((exec) => (
              <div key={exec.id} className="flex items-center gap-4 py-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  exec.pass_rate >= 80 ? 'bg-emerald-500/10' :
                  exec.pass_rate >= 50 ? 'bg-yellow-500/10' : 'bg-red-500/10'
                }`}>
                  {exec.pass_rate >= 80 ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : exec.pass_rate >= 50 ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-white font-medium">{exec.entity_id || 'N/A'}</p>
                    {exec.framework && (
                      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded font-mono">
                        {exec.framework}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    <span className="text-emerald-400">{exec.passed} pass</span>
                    {' · '}
                    <span className="text-red-400">{exec.failed} fail</span>
                    {' · '}
                    {exec.total_tests} total
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${passRateColor(exec.pass_rate)}`}>
                    {exec.pass_rate}%
                  </span>
                  <p className="text-xs text-slate-500">{getTimeAgo(exec.received_at)}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Play className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Sin ejecuciones registradas</p>
              <p className="text-slate-500 text-xs mt-1">Ejecuta tests desde Cursor con el MCP para ver resultados aquí</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ==========================================
// Sub-components
// ==========================================

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.FC<{ className?: string }>;
  color: 'blue' | 'green' | 'purple' | 'yellow' | 'red';
  isHighlight?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title, value, subtitle, icon: Icon, color, isHighlight
}) => {
  const colors: Record<string, string> = {
    blue: 'text-blue-400',
    green: 'text-emerald-400',
    purple: 'text-purple-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
  };

  return (
    <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-400">{title}</p>
        <Icon className={`w-5 h-5 ${colors[color]}`} />
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-3xl font-bold ${isHighlight ? colors[color] : 'text-white'}`}>
          {value}
        </span>
      </div>
      {subtitle && (
        <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
      )}
    </div>
  );
};


interface CoverageBarProps {
  label: string;
  current: number;
  total: number;
  color: 'blue' | 'purple' | 'green';
}

const CoverageBar: React.FC<CoverageBarProps> = ({ label, current, total, color }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  const barColors: Record<string, string> = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-emerald-500',
  };
  const dotColors: Record<string, string> = {
    blue: 'bg-blue-400',
    purple: 'bg-purple-400',
    green: 'bg-emerald-400',
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dotColors[color]}`}></span>
          <span className="text-sm text-slate-300">{label}</span>
        </div>
        <span className="text-sm font-semibold text-white">{current}/{total} ({pct}%)</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColors[color]} rounded-full transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};


const EntityRow: React.FC<{ entity: EntityCoverageItem }> = ({ entity }) => {
  const passRateColor = (rate: number) =>
    rate >= 80 ? 'text-emerald-400 bg-emerald-500/10' :
    rate >= 50 ? 'text-yellow-400 bg-yellow-500/10' :
    'text-red-400 bg-red-500/10';

  return (
    <tr className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
      <td className="py-2.5 px-3">
        <div>
          <span className="text-white font-medium">{entity.entity_id}</span>
          {entity.entity_name && (
            <span className="text-slate-500 ml-2 text-xs">{entity.entity_name}</span>
          )}
        </div>
      </td>
      <td className="text-center py-2.5 px-3">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${entity.has_code ? 'bg-emerald-400' : 'bg-slate-600'}`}></span>
      </td>
      <td className="text-center py-2.5 px-3">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${entity.has_executions ? 'bg-blue-400' : 'bg-slate-600'}`}></span>
      </td>
      <td className="text-center py-2.5 px-3">
        {entity.latest_pass_rate != null ? (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${passRateColor(entity.latest_pass_rate)}`}>
            {entity.latest_pass_rate}%
          </span>
        ) : (
          <span className="text-xs text-slate-600">—</span>
        )}
      </td>
      <td className="text-right py-2.5 px-3">
        <span className="text-sm text-slate-300">{entity.total_runs}</span>
      </td>
    </tr>
  );
};


function getTimeAgo(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Ahora';
  if (minutes < 60) return `${minutes} min`;
  if (hours < 24) return `${hours} hr`;
  return `${days} días`;
}

export default Dashboard;
